import { MessageItem } from '@/modules/assistant/parts/message-item';
import {
  assistantLogEmptyClasses,
  assistantMessageListClasses,
} from '@/modules/assistant/parts/styles';
import type { AssistantMessage } from '@/api/schemas/assistant';

export type MessageListProps = {
  /** The turns, oldest first. */
  messages: AssistantMessage[];
  /** The agent's name, from the route-aware context. */
  agentName: string;
  /** True while the turns are being read. */
  isLoading: boolean;
  /** The failure to show, or null when there is none. */
  error: string | null;
  /** Re-reads the turns. */
  onRetry: () => void;
};

/**
 * The conversation log.
 *
 * `role="log"` is the right role and not a decorative one: a log is a live
 * region that announces additions politely, so a new answer is read out when it
 * arrives WITHOUT interrupting whatever the person is doing elsewhere on the
 * page. An assertive announcement would talk over them.
 *
 * The four states stay apart, as they do everywhere else in the console: a
 * conversation that is loading says so, one that failed says so, one that is
 * empty says so, and none of them is presented as another.
 */
export function MessageList({ messages, agentName, isLoading, error, onRetry }: MessageListProps) {
  const isEmpty = !isLoading && error === null && messages.length === 0;

  return (
    <div
      role="log"
      aria-live="polite"
      aria-label="Conversation"
      className={assistantMessageListClasses}
    >
      {isLoading ? (
        <p role="status" className={assistantLogEmptyClasses}>
          Waiting for the assistant...
        </p>
      ) : null}

      {error === null ? null : (
        <div role="alert" className={assistantLogEmptyClasses}>
          <p>{error}</p>
          <button
            type="button"
            onClick={onRetry}
            className="mt-1 text-sm text-accent underline underline-offset-2"
          >
            Try again
          </button>
        </div>
      )}

      {isEmpty ? (
        <p className={assistantLogEmptyClasses}>This conversation has no messages yet.</p>
      ) : null}

      {messages.map((message) => (
        <MessageItem key={message.id} message={message} agentName={agentName} />
      ))}
    </div>
  );
}
